import { createReducer } from '@reduxjs/toolkit';
import {
  DISABLE_BOLD,
  DISABLE_ITALIC,
  DISABLE_NEXT_PAGE,
  ENABLE_BOLD,
  ENABLE_ITALIC,
  ENABLE_NEXT_PAGE,
  GET_FAILED_QR_DESIGN,
  GET_QR_DESIGN,
  GET_UNAUTHORIZED_QR_DESIGN,
  MOVE_TO_FIRST_PAGE,
  NEXT,
  PREVIOUS,
  SET_BACKGROUND_IMAGE,
  SET_CAPTION_COLOR,
  SET_CAPTION_POINT,
  SET_CAPTION_SIZE,
  SET_QRCODE_POINT,
  SET_QRCODE_SIZE,
} from 'admin/actions/qr-designer';
import { getNextPage, getPreviousPage } from 'admin/utils/qr-designer';
import { combineReducers } from 'redux';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants/qr-designer';

// Types
type Point = {
  x: number;
  y: number;
};

type Size = {
  width: number;
  height: number;
};

type Caption = Point & {
  size: number;
  color: string;
  align: string;
  bold: boolean;
  italic: boolean;
};

type QRCode = Point & { size: number };

export type QRDesignerState = {
  exists: boolean;
  status: 'unauthorized' | 'notFound' | 'success';
  size: Size;
  page: number;
  canNextPage: boolean;
  backgroundImage: string | null;
  qrcode: QRCode;
  caption: Caption;
};

// Initial states
const QRCODE_SIZE = 128;
const CAPTION_SIZE = 30;

const initialSize: Size = {
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
};

const initialQRCode: QRCode = {
  x: Math.round(CANVAS_WIDTH / 2 - QRCODE_SIZE / 2),
  y: Math.round(CANVAS_HEIGHT / 2 - QRCODE_SIZE / 2),
  size: QRCODE_SIZE,
};

const initialCaption: Caption = {
  x: Math.round(CANVAS_WIDTH / 2),
  y: Math.round(CANVAS_HEIGHT / 2 - CAPTION_SIZE / 2),
  size: CAPTION_SIZE,
  color: 'black',
  align: 'center',
  bold: true,
  italic: false,
};

// Reducers
const exists = createReducer(false, (builder) => {
  builder.addCase(GET_QR_DESIGN, () => true).addCase(GET_FAILED_QR_DESIGN, () => false);
});

const status = createReducer<QRDesignerState['status']>('success', (builder) => {
  builder
    .addCase(GET_QR_DESIGN, () => 'success')
    .addCase(GET_FAILED_QR_DESIGN, () => 'notFound')
    .addCase(GET_UNAUTHORIZED_QR_DESIGN, () => 'unauthorized');
});

const size = createReducer(initialSize, (builder) => builder);

const page = createReducer(0, (builder) =>
  builder
    .addCase(MOVE_TO_FIRST_PAGE, () => 0)
    .addCase(NEXT, (state) => getNextPage(state))
    .addCase(PREVIOUS, (state) => getPreviousPage(state))
);

const canNextPage = createReducer(false, (builder) =>
  builder.addCase(ENABLE_NEXT_PAGE, () => true).addCase(DISABLE_NEXT_PAGE, () => false)
);

const backgroundImage = createReducer<string | null>(null, (builder) =>
  builder
    .addCase(SET_BACKGROUND_IMAGE, (_, action) => action.payload)
    .addCase(GET_QR_DESIGN, (_, action) => action.payload.background_image)
);

const qrcode = createReducer(initialQRCode, (builder) =>
  builder
    .addCase(SET_QRCODE_POINT, (state, action) => {
      return { ...state, x: action.payload.x, y: action.payload.y };
    })
    .addCase(SET_QRCODE_SIZE, (state, action) => {
      return { ...state, size: action.payload };
    })
    .addCase(GET_QR_DESIGN, (state, action) => {
      return {
        ...state,
        x: action.payload.qrcode_x,
        y: action.payload.qrcode_y,
        size: action.payload.qrcode_size,
      };
    })
);

const caption = createReducer(initialCaption, (builder) =>
  builder
    .addCase(SET_CAPTION_POINT, (state, action) => {
      return { ...state, x: action.payload.x, y: action.payload.y };
    })
    .addCase(SET_CAPTION_SIZE, (state, action) => {
      return { ...state, size: action.payload };
    })
    .addCase(SET_CAPTION_COLOR, (state, action) => {
      return { ...state, color: action.payload };
    })
    .addCase(GET_QR_DESIGN, (state, action) => {
      return {
        ...state,
        x: action.payload.caption_x,
        y: action.payload.caption_y,
        size: action.payload.caption_size,
        color: action.payload.caption_color,
      };
    })
    .addCase(ENABLE_BOLD, (state) => {
      return { ...state, bold: true };
    })
    .addCase(DISABLE_BOLD, (state) => {
      return { ...state, bold: false };
    })
    .addCase(ENABLE_ITALIC, (state) => {
      return { ...state, italic: true };
    })
    .addCase(DISABLE_ITALIC, (state) => {
      return { ...state, italic: false };
    })
);

export default combineReducers({
  exists,
  status,
  size,
  page,
  canNextPage,
  backgroundImage,
  qrcode,
  caption,
});
